// @ts-check
// Regras puras das telas das campanhas de e-mail (spec/email.md seções 19.6 a 19.8; contrato 09 de e-mail, seção 16):
// cor de cada situação, filtros da lista, ações permitidas, ritmo de atualização, passos do assistente pela revisão e o
// agendamento no fuso do membro. Sem DOM: os testes de cliente e o modo demonstração usam as mesmas regras.
import { CAMPAIGN_SCHEDULE_WINDOW, EMAIL_NOT_RESUMABLE_PAUSE_REASONS, EMAIL_PROTECTION_PAUSE_REASONS } from '../../../shared/email/email-settings.js';
import { reviewCheckStep } from '../../../shared/email/campaign-review.js';
import { localParts, localToInstant } from '../../../shared/time/zoned-time.js';

/**
 * @typedef {'DRAFT' | 'SCHEDULED' | 'PREPARING' | 'SENDING' | 'PAUSED' | 'SENT' | 'SENT_WITH_ERRORS' | 'CANCELED' | 'FAILED'} EmailCampaignStatus
 * @typedef {{ id: string, status: string, pauseReason?: string | null, sentAt?: string | null, startedAt?: string | null,
 *   scheduledAt?: string | null, updatedAt?: string, formatVersion?: number }} CampaignLike
 * @typedef {'open' | 'report' | 'duplicate' | 'unschedule' | 'pause' | 'resume' | 'cancel' | 'delete' | 'backToDraft'} EmailCampaignAction
 * @typedef {'audience' | 'sender' | 'content' | 'review'} WizardStep
 */

/** Cor do selo de cada situação (spec/email.md seção 19.6). */
export const EMAIL_CAMPAIGN_STATUS_TONES = Object.freeze(/** @type {Record<EmailCampaignStatus, string>} */ ({
  DRAFT: 'gray', SCHEDULED: 'blue', PREPARING: 'yellow', SENDING: 'yellow', PAUSED: 'orange', SENT: 'green', SENT_WITH_ERRORS: 'orange',
  CANCELED: 'gray', FAILED: 'red',
}));

/** @param {string} status */
export const emailCampaignStatusTone = (status) => /** @type {Record<string, string>} */ (EMAIL_CAMPAIGN_STATUS_TONES)[status] ?? 'gray';

/** @param {string} campaignId */
export const emailCampaignHref = (campaignId) => `/campaigns/email/${encodeURIComponent(campaignId)}`;

/** Filtros da lista: cada um junta situações parecidas, para a lista não ter nove chips. */
export const EMAIL_CAMPAIGN_LIST_FILTERS = Object.freeze(/** @type {const} */ (['ALL', 'DRAFT', 'SCHEDULED', 'IN_PROGRESS', 'DONE', 'STOPPED']));
/** @typedef {typeof EMAIL_CAMPAIGN_LIST_FILTERS[number]} EmailCampaignListFilter */

const STATUSES_BY_FILTER = Object.freeze(/** @type {Record<EmailCampaignListFilter, readonly string[] | null>} */ ({
  ALL: null, DRAFT: ['DRAFT'], SCHEDULED: ['SCHEDULED'], IN_PROGRESS: ['PREPARING', 'SENDING', 'PAUSED'], DONE: ['SENT', 'SENT_WITH_ERRORS'],
  STOPPED: ['CANCELED', 'FAILED'],
}));

/** @param {unknown} value @returns {EmailCampaignListFilter} */
export function readListFilter(value) {
  return EMAIL_CAMPAIGN_LIST_FILTERS.includes(/** @type {any} */ (value)) ? /** @type {EmailCampaignListFilter} */ (value) : 'ALL';
}

/**
 * Campanhas do filtro e da busca (pelo nome, sem diferença de maiúsculas e acentos).
 * @template {CampaignLike & { name: string }} TCampaign
 * @param {TCampaign[]} campaigns @param {{ filter: EmailCampaignListFilter, search: string }} query
 * @returns {TCampaign[]}
 */
export function filterEmailCampaigns(campaigns, query) {
  const statuses = STATUSES_BY_FILTER[query.filter];
  const search = normalizeSearch(query.search);
  return campaigns.filter((campaign) => (!statuses || statuses.includes(campaign.status)) && (!search || normalizeSearch(campaign.name).includes(search)));
}

/** @param {string} text */
const normalizeSearch = (text) => String(text ?? '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

/** Pausa que só a proteção cria: retomar pede WORKSPACE e a confirmação do risco. @param {CampaignLike} campaign */
export const resumeNeedsAcknowledgement = (campaign) => EMAIL_PROTECTION_PAUSE_REASONS.includes(String(campaign.pauseReason ?? ''));

/** A escola não retoma a suspensão nem o conteúdo recusado. @param {CampaignLike} campaign */
export const canResumeEmailCampaign = (campaign) => campaign.status === 'PAUSED' && !EMAIL_NOT_RESUMABLE_PAUSE_REASONS.includes(String(campaign.pauseReason ?? ''));

/**
 * Ações do menu da lista e dos botões da campanha, na ordem da tela (spec/email.md seção 19.6). As regras são as do
 * motor; a tela só não oferece o que seria recusado.
 * @param {CampaignLike} campaign @returns {EmailCampaignAction[]}
 */
export function emailCampaignActions(campaign) {
  /** @type {EmailCampaignAction[]} */
  const actions = ['open', 'duplicate'];
  const { status } = campaign;
  if (status !== 'DRAFT' && status !== 'SCHEDULED' && status !== 'FAILED') actions.push('report');
  if (status === 'SCHEDULED') actions.push('unschedule');
  if (status === 'SENDING') actions.push('pause');
  if (canResumeEmailCampaign(campaign)) actions.push('resume');
  if (['SCHEDULED', 'PREPARING', 'SENDING', 'PAUSED'].includes(status)) actions.push('cancel');
  if (status === 'FAILED') actions.push('backToDraft');
  if (!['PREPARING', 'SENDING', 'PAUSED'].includes(status)) actions.push('delete');
  return actions;
}

/** Atualização da lista: a cada 15 s enquanto alguma prepara ou envia (K30). @param {CampaignLike[]} campaigns */
export function listRefreshMilliseconds(campaigns) {
  return campaigns.some((campaign) => campaign.status === 'PREPARING' || campaign.status === 'SENDING') ? 15_000 : null;
}

const HOUR = 3_600_000;
const FOLLOW_UP_HOURS = 48;

/**
 * Atualização do relatório (spec/email.md seção 19.8): 5 s enquanto prepara ou envia; 60 s agendada, pausada e nas
 * primeiras 48 h depois de terminar (aberturas e cliques chegam depois); parada fora disso.
 * @param {CampaignLike} campaign @param {number} [now]
 */
export function reportRefreshMilliseconds(campaign, now = Date.now()) {
  if (campaign.status === 'PREPARING' || campaign.status === 'SENDING') return 5_000;
  if (campaign.status === 'SCHEDULED' || campaign.status === 'PAUSED') return 60_000;
  if (['SENT', 'SENT_WITH_ERRORS', 'CANCELED'].includes(campaign.status)) {
    const finishedAt = Date.parse(String(campaign.sentAt ?? campaign.updatedAt ?? ''));
    if (Number.isFinite(finishedAt) && now - finishedAt < FOLLOW_UP_HOURS * HOUR) return 60_000;
  }
  return null;
}

/** Vista da página da campanha pela situação (spec/email.md seção 19.7). @param {CampaignLike} campaign */
export function emailCampaignPageView(campaign) {
  if (campaign.status === 'DRAFT') return Number(campaign.formatVersion ?? 2) < 2 ? 'UPGRADE' : 'WIZARD';
  if (campaign.status === 'SCHEDULED') return 'SCHEDULED';
  if (campaign.status === 'FAILED') return 'FAILED';
  return 'REPORT';
}

export const WIZARD_STEPS = Object.freeze(/** @type {const} */ (['audience', 'sender', 'content', 'review']));

/** @param {unknown} value @returns {WizardStep} */
export const readWizardStep = (value) => (WIZARD_STEPS.includes(/** @type {any} */ (value)) ? /** @type {WizardStep} */ (value) : 'audience');

/**
 * Ponto de cada passo pela última revisão: vermelho com erro, verde sem erro, nada antes de revisar.
 * @param {Array<{ code: string, severity: string }> | null} checks @returns {Record<WizardStep, 'ERROR' | 'OK' | null>}
 */
export function wizardStepStates(checks) {
  /** @type {Record<WizardStep, 'ERROR' | 'OK' | null>} */
  const states = { audience: null, sender: null, content: null, review: null };
  if (!checks) return states;
  for (const step of WIZARD_STEPS) states[step] = 'OK';
  for (const check of checks) if (check.severity === 'ERROR') states[reviewCheckStep(check.code)] = 'ERROR';
  return states;
}

/** @param {number | null | undefined} rate fração de 0 a 1 @param {(value: number) => string} formatPercent */
export function formatRate(rate, formatPercent) {
  return rate === null || rate === undefined || !Number.isFinite(rate) ? '-' : formatPercent(Math.round(rate * 1000) / 10);
}

const MINUTE = 60_000;
const DAY = 86_400_000;
const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

/** Horários de 5 em 5 minutos ("00:00" a "23:55"). */
export function scheduleTimeOptions() {
  const step = CAMPAIGN_SCHEDULE_WINDOW.stepMinutes;
  return Array.from({ length: (24 * 60) / step }, (_, index) => `${pad(Math.floor((index * step) / 60))}:${pad((index * step) % 60)}`);
}

/**
 * Primeiro horário que a tela oferece: o próximo de 5 em 5 minutos depois de agora mais 15 minutos, no fuso do membro.
 * @param {number} now @param {string} timeZone @returns {{ date: string, time: string }}
 */
export function defaultScheduleChoice(now, timeZone) {
  const step = CAMPAIGN_SCHEDULE_WINDOW.stepMinutes * MINUTE;
  const instant = Math.ceil((now + 15 * MINUTE) / step) * step;
  const local = localParts(instant, timeZone);
  return { date: `${local.year}-${pad(local.month)}-${pad(local.day)}`, time: `${pad(local.hour)}:${pad(local.minute)}` };
}

/** Limites do campo de data, no fuso do membro. @param {number} now @param {string} timeZone */
export function scheduleDateBounds(now, timeZone) {
  const toDate = (/** @type {number} */ instant) => {
    const local = localParts(instant, timeZone);
    return `${local.year}-${pad(local.month)}-${pad(local.day)}`;
  };
  return { min: toDate(now + CAMPAIGN_SCHEDULE_WINDOW.minimumMinutes * MINUTE), max: toDate(now + CAMPAIGN_SCHEDULE_WINDOW.maximumDays * DAY) };
}

/**
 * Data e hora escolhidas no fuso do membro viram o instante em UTC, dentro da janela de K13 (10 minutos a 90 dias).
 * @param {{ date: string, time: string }} choice @param {string} timeZone @param {number} now
 * @returns {{ scheduledAt: string | null, problem: 'EMPTY' | 'INVALID' | 'TOO_SOON' | 'TOO_FAR' | null }}
 */
export function readScheduleChoice(choice, timeZone, now) {
  if (!choice.date || !choice.time) return { scheduledAt: null, problem: 'EMPTY' };
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(choice.date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(choice.time);
  if (!dateMatch || !timeMatch) return { scheduledAt: null, problem: 'INVALID' };
  const [year, month, day] = dateMatch.slice(1).map(Number);
  const [hour, minute] = timeMatch.slice(1).map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return { scheduledAt: null, problem: 'INVALID' };
  const instant = localToInstant(year, month, day, hour, minute, timeZone);
  if (!Number.isFinite(instant)) return { scheduledAt: null, problem: 'INVALID' };
  if (instant < now + CAMPAIGN_SCHEDULE_WINDOW.minimumMinutes * MINUTE) return { scheduledAt: null, problem: 'TOO_SOON' };
  if (instant > now + CAMPAIGN_SCHEDULE_WINDOW.maximumDays * DAY) return { scheduledAt: null, problem: 'TOO_FAR' };
  return { scheduledAt: new Date(instant).toISOString(), problem: null };
}

/**
 * Situação da linha de cada destinatário: a mais adiantada vale (clicou, abriu, entregue...).
 * @param {{ status: string, deliveredAt?: string | null, firstOpenedAt?: string | null, firstClickedAt?: string | null,
 *   unsubscribedAt?: string | null, complainedAt?: string | null, isMachineOpenOnly?: boolean }} recipient
 * @returns {'COMPLAINED' | 'UNSUBSCRIBED' | 'CLICKED' | 'OPENED' | 'OPENED_ESTIMATED' | 'DELIVERED' | 'SENT' | 'BOUNCED' | 'FAILED' | 'SKIPPED' | 'QUEUED'}
 */
export function recipientDisplayStatus(recipient) {
  if (recipient.complainedAt) return 'COMPLAINED';
  if (recipient.unsubscribedAt) return 'UNSUBSCRIBED';
  if (recipient.status === 'BOUNCED') return 'BOUNCED';
  if (recipient.status === 'FAILED') return 'FAILED';
  if (recipient.status === 'SKIPPED') return 'SKIPPED';
  if (['HELD', 'QUEUED', 'SENDING'].includes(recipient.status)) return 'QUEUED';
  if (recipient.firstClickedAt) return 'CLICKED';
  if (recipient.firstOpenedAt) return recipient.isMachineOpenOnly ? 'OPENED_ESTIMATED' : 'OPENED';
  if (recipient.deliveredAt || recipient.status === 'DELIVERED') return 'DELIVERED';
  return 'SENT';
}

/** Cor da situação do destinatário. @param {ReturnType<typeof recipientDisplayStatus>} status */
export function recipientStatusTone(status) {
  if (status === 'CLICKED' || status === 'OPENED' || status === 'DELIVERED') return 'green';
  if (status === 'OPENED_ESTIMATED' || status === 'SENT' || status === 'QUEUED') return 'blue';
  if (status === 'BOUNCED' || status === 'UNSUBSCRIBED') return 'orange';
  if (status === 'COMPLAINED' || status === 'FAILED') return 'red';
  return 'gray';
}

/**
 * Entregues mostrados: com os avisos do provedor, os medidos; sem eles, a taxa estimada (enviados menos devolvidos)
 * vezes os enviados, como o motor calcula.
 * @param {{ counts: { sent: number, delivered: number }, rates: { deliveryRate: number | null } }} campaign
 */
/**
 * Campanha enviada antes da 2.0.0 (formato 1, sem versão congelada): entregues, aberturas e cliques não foram medidos e
 * a lista mostra traço, não zero.
 * @param {{ formatVersion?: number, contentId?: string | null }} campaign
 */
export const hasUnmeasuredEngagement = (campaign) => campaign.formatVersion === 1 && !campaign.contentId;

export function displayedDelivered(campaign) {
  if (campaign.rates.deliveryRate === null || campaign.counts.sent === 0) return campaign.counts.delivered;
  return Math.round(campaign.rates.deliveryRate * campaign.counts.sent);
}

/** Endereço longo encurtado na tabela de links (o completo fica no título). @param {string} url @param {number} [maximum] */
export function shortenUrl(url, maximum = 48) {
  const text = String(url ?? '').replace(/^https?:\/\//, '');
  return text.length > maximum ? `${text.slice(0, maximum - 1)}…` : text;
}

/**
 * Frase e link do item "E-mail de marketing" da linha do tempo (spec/email.md seção 16.1): o assunto gravado no envio,
 * a campanha ou o fluxo de origem e o link só para quem tem a flag de cada um.
 * @param {{ subject?: string | null, source?: { kind: 'CAMPAIGN' | 'FLOW', id: string | null, name: string | null, canOpen: boolean } | null }} properties
 * @param {(key: string, values?: Record<string, unknown>) => string} translate
 * @returns {{ text: string, href: string | null, linkLabel: string | null }}
 */
export function describeEmailMarketingEvent(properties, translate) {
  const subject = `"${properties.subject || translate('timeline.emailMarketing.noSubject')}"`;
  const source = properties.source ?? null;
  if (!source?.name) return { text: translate('timeline.emailMarketing.withoutSource', { subject }), href: null, linkLabel: null };
  const isCampaign = source.kind === 'CAMPAIGN';
  const text = translate(isCampaign ? 'timeline.emailMarketing.fromCampaign' : 'timeline.emailMarketing.fromFlow', { subject, name: source.name });
  if (!source.canOpen || !source.id) return { text, href: null, linkLabel: null };
  return {
    text,
    href: isCampaign ? emailCampaignHref(source.id) : `/campaigns/flows/${encodeURIComponent(source.id)}`,
    linkLabel: translate(isCampaign ? 'timeline.emailMarketing.openCampaign' : 'timeline.emailMarketing.openFlow'),
  };
}
